/* eslint-disable */
import { CloseOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Badge, Button, message, Tag, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { rootAPI } from '../../../config/api';

const { Title, Text } = Typography;

// ══════════════════════════════════════════════════════════════════════
// 🎧 কাস্টমারের অডিও প্লেয়ার (8000Hz Telephony)
// ══════════════════════════════════════════════════════════════════════
class TelephonyPCMPlayer {
  constructor() {
    this.audioCtx = null;
    this.scriptNode = null;
    this.audioQueue = [];
    this.isRunning = false;
  }

  start(sampleRate = 8000) {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    this.audioCtx = new AudioContext({ sampleRate: sampleRate });
    this.audioQueue = [];
    this.isRunning = true;

    this.scriptNode = this.audioCtx.createScriptProcessor(1024, 1, 1);
    this.scriptNode.onaudioprocess = (e) => {
      const output = e.outputBuffer.getChannelData(0);
      for (let i = 0; i < output.length; i++) {
        output[i] = this.audioQueue.length > 0 ? this.audioQueue.shift() : 0;
      }
    };
    this.scriptNode.connect(this.audioCtx.destination);
  }

  feedBase64(base64Data) {
    if (!this.isRunning || !this.audioCtx) return;

    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }

    try {
      const binaryString = window.atob(base64Data);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const int16 = new Int16Array(bytes.buffer);
      for (let i = 0; i < int16.length; i++) {
        this.audioQueue.push(int16[i] / 32768.0);
      }

      if (this.audioQueue.length > 16000) {
        this.audioQueue.splice(0, this.audioQueue.length - 8000);
      }
    } catch (e) {}
  }

  stop() {
    this.isRunning = false;
    this.audioQueue = [];
    if (this.scriptNode) {
      this.scriptNode.disconnect();
      this.scriptNode = null;
    }
    if (this.audioCtx) {
      this.audioCtx.close();
      this.audioCtx = null;
    }
  }
}

export default function LiveAgentCallModal({ appId }) {
  const [incomingCall, setIncomingCall] = useState(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const socketRef = useRef(null);
  const playerRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recorderCtxRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const debugCounter = useRef(0);

  useEffect(() => {
    if (!appId) return;

    const socket = io(`${rootAPI}`, {
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join_campaign_room', appId);
    });

    socket.on('incoming_agent_call', (data) => {
      console.log('🔔 [Incoming Call Received]:', data);
      setIncomingCall(data);
      playRingtoneSound();
    });

    socket.on('call_ended_for_agent', () => {
      message.info('Customer hung up the call.');
      handleEndCall();
    });

    // কাস্টমারের অডিও প্লে করা
    socket.on('customer_audio_stream', (payload) => {
      const base64Chunk = typeof payload === 'string' ? payload : payload.audioChunk;
      if (playerRef.current && base64Chunk) {
        playerRef.current.feedBase64(base64Chunk);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [appId]);

  useEffect(() => {
    if (isCallActive) {
      setCallDuration(0);
      timerIntervalRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    }
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [isCallActive]);

  // ══════════════════════════════════════════════════════════════════════
  // 🎤 কল একসেপ্ট করা (মাইক্রোফোন ক্যাপচার + ইকো সাপ্রেশন ফিক্স)
  // ══════════════════════════════════════════════════════════════════════
  const handleAcceptCall = async () => {
    if (!incomingCall || !socketRef.current) return;

    try {
      // ১. কাস্টমারের সাউন্ড শোনার প্লেয়ার
      const player = new TelephonyPCMPlayer();
      player.start(8000);
      playerRef.current = player;

      // ২. এজেন্টের মাইক্রোফোন ক্যাপচার
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      mediaStreamRef.current = stream;

      const track = stream.getAudioTracks()[0];
      console.log('🎤 Active Mic Device:', track.label);

      // ৩. ব্রাউজার অডিও কনটেক্সট চালু
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContext();
      recorderCtxRef.current = audioCtx;
      await audioCtx.resume();

      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(2048, 1, 1);

      // 🆕 লোকাল ইকো মিউট করার জন্য সাইলেন্ট গেইন নোড
      const silentGain = audioCtx.createGain();
      silentGain.gain.value = 0; // এটি স্পিকারে ফিডব্যাক আটকে মাইক আনমিউট রাখে

      const nativeRate = audioCtx.sampleRate; // e.g. 48000
      const targetRate = 24000; // SIP Streamer এর রেট
      const step = nativeRate / targetRate;

      processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);

        // 🆕 24000Hz এ পারফেক্ট কনভার্সন
        const outLength = Math.floor(inputData.length / step);
        const pcm16 = new Int16Array(outLength);

        for (let i = 0; i < outLength; i++) {
          const sample = inputData[Math.floor(i * step)];
          pcm16[i] = Math.max(-1, Math.min(1, sample)) * 0x7fff;
        }

        debugCounter.current++;
        if (debugCounter.current % 50 === 0) {
          const maxAmp = Math.max(...inputData.map(Math.abs));
          console.log(
            '🎙️ Agent Mic Volume (Amplitude):',
            maxAmp.toFixed(3),
            maxAmp > 0.02 ? '✅ Voice Detected' : '⚠️ Quiet/Silence'
          );
        }

        const base64Chunk = btoa(String.fromCharCode(...new Uint8Array(pcm16.buffer)));

        // ফোনের স্পিকারে এজেন্টের অডিও পাঠানো
        socketRef.current.emit('agent_audio_stream', {
          handleId: incomingCall.handleId,
          audioChunkBase64: base64Chunk,
        });
      };

      // 🆕 সংযোগ স্থাপন (ইকো ব্লক প্রতিরোধ করে)
      source.connect(processor);
      processor.connect(silentGain);
      silentGain.connect(audioCtx.destination);

      socketRef.current.emit('agent_accept_call', {
        handleId: incomingCall.handleId,
        appId: incomingCall.appId,
      });

      setIsCallActive(true);
      message.success('Call Connected! Speak now.');
    } catch (err) {
      alert('Microphone error: ' + err.message);
    }
  };

  const handleEndCall = () => {
    if (incomingCall && socketRef.current) {
      socketRef.current.emit('agent_hangup_call', { handleId: incomingCall.handleId });
    }

    if (playerRef.current) {
      playerRef.current.stop();
      playerRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }

    if (recorderCtxRef.current) {
      try { recorderCtxRef.current.close(); } catch (e) {}
      recorderCtxRef.current = null;
    }

    setIncomingCall(null);
    setIsCallActive(false);
  };

  const playRingtoneSound = () => {
    try {
      const ringAudio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
      ringAudio.play().catch(() => {});
    } catch (e) {}
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!incomingCall) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '360px',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 12px 36px rgba(0, 0, 0, 0.25)',
        border: '2px solid #1677ff',
        padding: '20px',
        zIndex: 999999,
        textAlign: 'center',
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Badge dot status={isCallActive ? 'success' : 'processing'} offset={[-5, 5]}>
          <Avatar
            size={64}
            icon={<UserOutlined />}
            style={{
              backgroundColor: isCallActive ? '#52c41a' : '#1677ff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          />
        </Badge>
      </div>

      <Title level={4} style={{ margin: '0 0 4px 0' }}>
        {incomingCall.customerNumber}
      </Title>

      <Text type="secondary" style={{ display: 'block', marginBottom: 10 }}>
        {isCallActive ? (
          <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
            🔴 Live Connected &bull; {formatTimer(callDuration)}
          </span>
        ) : (
          '📞 Incoming AI Handover Call'
        )}
      </Text>

      <Tag color="orange" style={{ marginBottom: 16 }}>
        Reason: {incomingCall.reason || 'Human Assistance'}
      </Tag>

      <div>
        {!isCallActive ? (
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Button
              type="primary"
              shape="round"
              size="large"
              icon={<PhoneOutlined />}
              onClick={handleAcceptCall}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', flex: 1 }}
            >
              Accept
            </Button>
            <Button
              type="primary"
              danger
              shape="round"
              size="large"
              icon={<CloseOutlined />}
              onClick={handleEndCall}
              style={{ flex: 1 }}
            >
              Decline
            </Button>
          </div>
        ) : (
          <Button
            type="primary"
            danger
            shape="round"
            size="large"
            icon={<PhoneOutlined style={{ transform: 'rotate(135deg)' }} />}
            onClick={handleEndCall}
            style={{ width: '100%' }}
          >
            End Call
          </Button>
        )}
      </div>
    </div>
  );
}